import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import { DisplayModeIds } from "constants/NotificationDisplayModeConstants";
import { useIntl } from "react-intl";

type InAppPreviewProps = {
  displayModeId?: number;
  title?: string;
  body?: string;
  actionUrl?: string;
  actionLabel?: string;
  fullWidth?: boolean;
};

const renderBodyWithVariables = (text?: string) => {
  if (!text) return null;
  const parts = text.split(/(\[\[[^\]]+\]\])/g);
  return parts.map((part, i) =>
    part.startsWith("[[") && part.endsWith("]]") ? (
      <strong key={i}>{part}</strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
};

const PopupPreview = ({ title, body, actionLabel }: InAppPreviewProps) => (
  <Paper
    elevation={4}
    sx={{
      maxWidth: 380,
      mx: "auto",
      borderRadius: 2,
      overflow: "hidden",
    }}
  >
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h6"
        sx={{ color: "primary.main", fontWeight: 500, mb: 1 }}
      >
        {title || "Notification Title"}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.primary",
          mb: 2,
          lineHeight: 1.55,
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}
      >
        {renderBodyWithVariables(body) || "Notification body text..."}
      </Typography>
      {actionLabel && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            size="small"
            sx={{ textTransform: "uppercase" }}
          >
            {actionLabel}
          </Button>
        </Box>
      )}
    </Box>
  </Paper>
);

const PanelPreview = ({ title, body }: InAppPreviewProps) => (
  <Paper
    elevation={2}
    sx={{ maxWidth: 340, mx: "auto", borderRadius: 2, overflow: "hidden" }}
  >
    <Box
      sx={{
        p: 1.5,
        bgcolor: "#1D3E4E",
        color: "#fff",
        fontWeight: 500,
        fontSize: 13,
      }}
    >
      Notifications
    </Box>
    <Box sx={{ p: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontSize: 13, fontWeight: 500, mb: 0.25 }}
        >
          {title || "Notification Title"}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.45 }}
        >
          {renderBodyWithVariables(body) || "Notification body..."}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: 11,
            color: "text.disabled",
            mt: 0.5,
            display: "block",
          }}
        >
          Just now
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const BannerPreview = ({ title, body, fullWidth }: InAppPreviewProps) => (
  <Box
    sx={{
      bgcolor: "primary.main",
      color: "#fff",
      px: 3,
      py: 1.5,
      borderRadius: 1,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      maxWidth: fullWidth ? "none" : 500,
      width: fullWidth ? "100%" : undefined,
      mx: "auto",
    }}
  >
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {title || "Notification Title"}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.92 }}>
        {renderBodyWithVariables(body) || "Notification body..."}
      </Typography>
    </Box>
    <IconButton size="small" sx={{ color: "#fff", opacity: 0.85 }} disabled>
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

const InAppPreview = (props: InAppPreviewProps) => {
  const { formatMessage: t } = useIntl();

  return (
    <Box sx={{ py: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 2, textAlign: "center", color: "text.secondary" }}
      >
        {t({ id: "livePreview" })}
      </Typography>
      <Box
        sx={{
          bgcolor: "grey.50",
          borderRadius: 2,
          p: 3,
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {props.displayModeId === DisplayModeIds.Panel ? (
          <PanelPreview {...props} />
        ) : props.displayModeId === DisplayModeIds.Banner ? (
          <BannerPreview {...props} fullWidth={props.fullWidth} />
        ) : (
          <PopupPreview {...props} />
        )}
      </Box>
    </Box>
  );
};

export default InAppPreview;
